import React, {ReactElement, useMemo} from 'react';
import {Typography, CardOkCardIcon} from '@customink/pigment-react';
import {Card, CardContent, CardMedia, Skeleton, Paper} from '@mui/material';
import {formatCurrency} from '../../utils/helpers';
import './index.scss';

const SKELETON_IMAGE_HEIGHT = 126;
const SKELETON_IMAGE_WIDTH = 136;
const SKELETON_SIZES_WIDTH = 128;
const SKELETON_NAME_WIDTH = 235;
const SKELETON_COLOR_WIDTH = 168;
const SKELETON_PRICE_WIDTH = 145;

type SizeMapType = Record<string, number>;

type SizeMapTupleType = [string, number];

type OrderProductCardSizesProps = {
  sizes: SizeMapTupleType[];
  placeholder?: boolean;
};

const OrderProductCardSizes: React.FC<OrderProductCardSizesProps> = ({
  sizes,
  placeholder
}: OrderProductCardSizesProps): ReactElement => {
  return (
    <>
      <Typography
        display="flex"
        className="orderProductCard-info-sizeCounts"
        variant="bodyShort1"
        weight="medium">
        {placeholder ? (
          <Skeleton width={SKELETON_SIZES_WIDTH} />
        ) : (
          sizes.map(sizeTuple => (
            <span
              key={sizeTuple[0]}
              className="orderProductCard-info-sizeCounts-span">{`${sizeTuple[0]} ${sizeTuple[1]}`}</span>
          ))
        )}
      </Typography>
    </>
  );
};

OrderProductCardSizes.defaultProps = {
  placeholder: false
};

type OrderProductCardProps = {
  productImage: string;
  productName: string;
  productColor: string;
  decorationMethod: string;
  unitPrice: string | number;
  isOrganizerContributionItem?: boolean;
  sizes: SizeMapType;
  mobile?: boolean;
  placeholder?: boolean;
};

const quantitySorter = (a: SizeMapTupleType, b: SizeMapTupleType) => {
  return b[1] - a[1];
};

const sortSizesByQuantity = (sizes: SizeMapType) => {
  const sizesKeys = Object.keys(sizes);
  return sizesKeys.length
    ? sizesKeys
        .map(size => [size, sizes[size]] as SizeMapTupleType)
        .sort(quantitySorter)
    : [];
};

const sumTotalQuantity = (sizes: SizeMapType) => {
  return Object.keys(sizes).reduce((prev, curr) => {
    return prev + sizes[curr];
  }, 0);
};

const OrderProductCard: React.FC<OrderProductCardProps> = ({
  isOrganizerContributionItem,
  productImage,
  productName,
  productColor,
  decorationMethod,
  unitPrice,
  sizes,
  mobile,
  placeholder
}: OrderProductCardProps): ReactElement => {
  const sortedSizeTuples = useMemo(() => sortSizesByQuantity(sizes), [sizes]);
  const totalQuantity = useMemo(() => sumTotalQuantity(sizes), [sizes]);

  const cardClasses = `orderProductCard${mobile ? ' mobile' : ''}`;

  const orderProductCardPrice = () => {
    return isOrganizerContributionItem ? (
      <>
        {formatCurrency(Number(unitPrice) || 0)}
        <br />
        Qty 1
      </>
    ) : (
      `Qty ${totalQuantity} @ ${formatCurrency(Number(unitPrice))} each`
    );
  };
  const orderProductCardImage = () => {
    return isOrganizerContributionItem ? (
      <Paper elevation={0} className="orderProductCard-productIconContainer">
        <CardOkCardIcon fontSize="large" className="productIcon" />
      </Paper>
    ) : (
      <CardMedia
        className="orderProductCard-productImage"
        component="img"
        image={productImage}
        alt={productName}
      />
    );
  };

  return (
    <Card className={cardClasses} elevation={0}>
      {placeholder ? (
        <Skeleton
          variant="rectangular"
          width={SKELETON_IMAGE_WIDTH}
          height={SKELETON_IMAGE_HEIGHT}>
          <CardMedia
            className="orderProductCard-productImage"
            component="img"
            alt="clothing product"
          />
        </Skeleton>
      ) : (
        orderProductCardImage()
      )}
      <CardContent className="orderProductCard-info">
        <Typography
          display="block"
          className="orderProductCard-info-name"
          variant="bodyShort1">
          {placeholder ? <Skeleton width={SKELETON_NAME_WIDTH} /> : productName}
        </Typography>

        {isOrganizerContributionItem ? null : (
          <Typography
            display="block"
            className="orderProductCard-info-color"
            variant="bodyShort1">
            {placeholder ? (
              <Skeleton width={SKELETON_COLOR_WIDTH} />
            ) : (
              `${productColor} | ${decorationMethod}`
            )}
          </Typography>
        )}

        <OrderProductCardSizes
          placeholder={placeholder}
          sizes={sortedSizeTuples}
        />
        <Typography
          display="block"
          className="orderProductCard-info-price"
          variant="bodyShort1">
          {placeholder ? (
            <Skeleton width={SKELETON_PRICE_WIDTH} />
          ) : (
            orderProductCardPrice()
          )}
        </Typography>
      </CardContent>
    </Card>
  );
};

OrderProductCard.defaultProps = {
  mobile: false,
  placeholder: false,
  isOrganizerContributionItem: false
};

export default OrderProductCard;
